import { useCallback, useContext, useEffect, useState } from "react"
import { UserContext } from "../../../../context/userContext"
import {
  kioskApiPostAssessmentsConfigs,
  kioskApiPostFTVOptionList,
} from "../../kioskApi"
import { LogError } from "../../../../helpers/logger"
import { LoaderMedium } from "../../../../components/Loaders"
import { Button } from "primereact/button"
import styles from "../../../../styles/kiosk/KioskAdditionalAssessment.module.css"
import kioskStyles from "../../../../styles/kiosk/KioskAdmin.module.css"
import ConfigModal from "./ConfigModal"
import InactiveKioskLabel from "../InactiveKioskLabel"
import CustomTable from "../CustomComponents/CustomTable"

const KioskAdditionalAssessmentsList = ({
  kioskOrganizationId,
  organizationId,
  kioskTemplates,
}) => {
  const [assessments, setAssessments] = useState([])
  const [assessmentsConfigs, setAssessmentsConfigs] = useState([])
  const [ftvOptions, setFtvOptions] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingFTVOptions, setIsLoadingFTVOptions] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [updateList, setUpdateList] = useState(true)
  const [editConfig, setEditConfig] = useState(null)
  const [deleteConfig, setDeleteConfig] = useState(false)

  const userCtx = useContext(UserContext)

  const getAssessments = useCallback(async () => {
    try {
      if (updateList) {
        setIsLoading(true)
        const additionalAssessment = await kioskApiPostAssessmentsConfigs(
          userCtx.tokenKiosk,
          {
            OrganizationId: organizationId,
            EnterpriseId: userCtx.session.user.enterprise_id,
            KioskOrganizationId: kioskOrganizationId,
          }
        )

        if (additionalAssessment) {
          const assessmentsMap = new Map(
            additionalAssessment.assessments?.map((a) => [a.id, a.name]) || []
          )

          let configs = (additionalAssessment?.configs || []).map((config) => {
            return {
              ...config,
              name: assessmentsMap.get(config?.additionalAssessmentId),
            }
          })

          setAssessments(additionalAssessment?.assessments)
          setAssessmentsConfigs(configs)
        } else {
          userCtx.setNotificationError("Kiosk assessment not found")
        }
        setUpdateList(false)
        setIsLoading(false)
      }
    } catch (error) {
      LogError("Unable to load Kiosk assessments profile", error)
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskOrganizationId, updateList])

  useEffect(() => {
    getAssessments()
  }, [getAssessments])

  const getFtvOptions = useCallback(async () => {
    try {
      setIsLoadingFTVOptions(true)
      const options = await kioskApiPostFTVOptionList(userCtx.tokenKiosk, {
        KioskOrganizationId: kioskOrganizationId,
        ActiveOnly: true,
      })
      if (options) {
        const templateMap = new Map(
          kioskTemplates?.map((t) => [t.template_name, t.template_type]) || []
        )

        const newOptions = options.map((o) => {
          return {
            value: o.id,
            name: o.name,
            soarTemplateName: o.soarTemplateName,
            soarTemplateType: templateMap.get(o.soarTemplateName),
          }
        })
        setFtvOptions(newOptions)
        setIsLoadingFTVOptions(false)
      } else {
        setFtvOptions([])
        setIsLoadingFTVOptions(false)
        userCtx.setNotificationError("Additional assessment not found")
      }
    } catch (error) {
      LogError("Unable to load first time visitor options", error)
      userCtx.setNotificationError("Unable to load First Time Visitor options")
      setIsLoadingFTVOptions(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskOrganizationId, kioskTemplates])

  useEffect(() => {
    getFtvOptions()
  }, [getFtvOptions])

  const actionsTemplate = (rowData) => {
    return (
      <>
        <Button
          icon="pi pi-pencil"
          className={`${kioskStyles.roundedButton} ${kioskStyles.editButton}`}
          tooltip="edit"
          type="button"
          onClick={() => {
            setEditConfig(rowData)
            setShowModal(true)
          }}
          tooltipOptions={{ position: "bottom", className: "custom-tooltip" }}
        />
        <Button
          icon="pi pi-trash"
          className={`${kioskStyles.roundedButton} ${kioskStyles.deleteButton}`}
          tooltip="delete"
          type="button"
          onClick={() => {
            setDeleteConfig(true)
            setEditConfig(rowData)
            setShowModal(true)
          }}
          tooltipOptions={{ position: "bottom", className: "custom-tooltip" }}
        />
      </>
    )
  }
  const enabledTemplate = (rowData) => {
    return (
      <div className={kioskStyles.tableData}>
        {rowData?.enabled ? "Yes" : "No"}
      </div>
    )
  }
  const additionalFormTemplate = (rowData) => {
    return (
      <div className={kioskStyles.tableData}>
        {rowData.name || "[Unknown Assessment]"}
      </div>
    )
  }
  const descriptionTemplate = (rowData) => {
    return <div className={kioskStyles.tableData}>{rowData.description}</div>
  }

  const headers = () => {
    return [
      {
        value: "name",
        label: "Assessment Type",
        dataTemplate: additionalFormTemplate,
      },
      {
        value: "description",
        label: "Description",
        dataTemplate: descriptionTemplate,
      },
      {
        value: "enabled",
        label: "Enabled",
        dataTemplate: enabledTemplate,
        className: styles.statusColumn,
      },

      {
        value: "",
        label: "",
        dataTemplate: actionsTemplate,
        className: styles.actionColumn,
      },
    ]
  }

  return (
    <>
      <div className={styles.header}>
        <Button
          label="New Kiosk Assessment"
          onClick={() => {
            setDeleteConfig(false)
            setEditConfig(null)
            setShowModal(true)
          }}
          disabled={isLoading || !kioskOrganizationId || isLoadingFTVOptions}
          className={`${styles.addConfigButton} ${kioskStyles.primaryButton}`}
        />
      </div>
      {isLoading || isLoadingFTVOptions ? (
        <LoaderMedium />
      ) : !kioskOrganizationId ? (
        <InactiveKioskLabel />
      ) : !assessments || assessments.length === 0 ? (
        <>Kiosk Assessments not found</>
      ) : (
        <CustomTable collection={assessmentsConfigs} headers={headers()} />
      )}

      <ConfigModal
        show={showModal}
        setShow={setShowModal}
        assessments={assessments}
        ftvOptions={ftvOptions}
        kiosk_organization_id={kioskOrganizationId}
        setUpdateList={setUpdateList}
        assessmentsConfigs={assessmentsConfigs}
        editConfig={editConfig}
        setEditConfig={setEditConfig}
        deleteConfig={deleteConfig}
        setDeleteConfig={setDeleteConfig}
      />
    </>
  )
}
export default KioskAdditionalAssessmentsList
